import { Injectable } from '@angular/core';
import { Asset } from '../types/Asset';
import { ContentfulAsset } from '../types/ContentfulAsset';
import { ContentfulItem } from '../types/ContentfulItem';
import { ContentfulModel } from '../types/ContentfulModel';
import { Type } from './../types/Type';
import { Platform } from '@ionic/angular';
import { HttpClient } from '@angular/common/http';
import packageJson from '../../../package.json';
import { Article } from '../types/Article';
import { Tip } from '../types/Tip';
import { Link } from '../types/Link';
import { Articles } from '../types/Articles';

@Injectable({
    providedIn: 'root',
})
export class AppDataParserService {
    private readonly possibleTypes: Type<ContentfulModel>[] = [
        Asset,
        Article,
        Tip,
        Link,
        Articles
    ];

    private items: ContentfulModel[] = [];

    constructor(
		private readonly httpClient: HttpClient,
		private platform: Platform
    ) {}

    parseFromJson() {
        try {
            const jsonItems = JSON.parse(
                localStorage.getItem('contentful') || '[]'
            ) as any[];
            this.items = jsonItems.map((item) =>
                this.getTypeFromContentType(item.type, item, true)
            ) as ContentfulModel[];

        } catch (err) {
            localStorage.removeItem('contentful');
        }
    }

    getById(id: string): ContentfulModel | null {
        return this.items.find((x) => x.getId() === id) ?? null;
    }

    getByType<T extends ContentfulModel = ContentfulModel>(type: string): T[] {
        return this.items.filter((x) => x.getType() === type) as T[];
    }

    getTypeFromContentType(
        contentType: string,
        content: object,
        fromJson = false
    ): ContentfulModel {
        for (const type of this.possibleTypes) {
            if (
                new type({}, this.getById.bind(this)).getType() === contentType
            ) {
                return new type(content, this.getById.bind(this), fromJson);
            }
        }
    }

    async init() {
        const isPreview = location.origin.includes('//preview--') || location.host === 'localhost:8888';
        const currentVersion = localStorage.getItem('version');
        const sameVersion = currentVersion === packageJson.version;
        const lastUpdated = new Date(localStorage.getItem('contentfulFetchDatetime'));

        if (localStorage.getItem('contentful') && sameVersion) {
            this.parseFromJson();
        }

        const response = await this.httpClient
            .get<{lastUpdateDatetime: Date}>(
                this.platform.is('hybrid')
                    ? 'https://preview--nepecht.netlify.app/.netlify/functions/updated'
                    : '/.netlify/functions/updated'
            )
            .toPromise();

        if (new Date(response.lastUpdateDatetime) <= lastUpdated) return;


        const fetchData = async (skip = 0) => {
            const result = await this.httpClient
                .get<{ items: ContentfulItem[]; includes: { Asset: ContentfulAsset[] }; total: number }>(
                    this.platform.is('hybrid')
                        ? 'https://preview--nepecht.netlify.app/.netlify/functions/entries'
                        : '/.netlify/functions/entries',
                    {
                        params: {
                            skip,
                            limit: 50,
                        },
                    }
                )
                .toPromise();;

            const items = result.items ?? [];
            const assets = result.includes?.Asset ?? [];

            items.forEach((item) => {
                this.items.push(this.parseItem(item));
            });
            assets.forEach((asset) => {
                const parsed = this.parseAsset(asset) as Asset;
                this.items.push(parsed);
            });
            const total = items.length;
            if (this.items.length < result.total) {
                return fetchData(skip + total);
            } else {
                localStorage.setItem('contentful', JSON.stringify(this.items));
                localStorage.setItem(
                    'contentfulFetchDatetime',
                    new Date().toISOString()
                );
                localStorage.setItem('version', packageJson.version);
                return window.location.reload();
            }
        };

        this.items = [];
        await fetchData();
    }

    parseItem(object: ContentfulItem): ContentfulModel {
        const contentType = object.sys.contentType.sys.id;
        const id = object.sys.id;
        const content = object.fields;

        return this.getTypeFromContentType(contentType, { id, ...content });
    }

    parseAsset(
        object: ContentfulAsset
    ): ContentfulModel {
        const id = object.sys.id;
        const content = object.fields;
        return this.getTypeFromContentType('asset', { id, ...content }) as Asset;
    }
}
